import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ShoppingCart, Package, ArrowLeft, Star, Shield, Truck } from "lucide-react";
import HTMLContent from "@/components/HTMLContent";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  stock: number;
  type: 'product' | 'service';
  category_name?: string;
  billing_type?: 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time';
}

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const { addToCart } = useCart();

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products_2025_11_10_14_11')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          stock,
          type,
          billing_type,
          categories_2025_11_10_14_11(name)
        `)
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      const formattedProduct = {
        ...data,
        category_name: data.categories_2025_11_10_14_11?.name
      };

      setProduct(formattedProduct);

      // Fetch related products
      if (data.categories_2025_11_10_14_11) {
        fetchRelatedProducts(data.category_id, data.id);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo cargar el producto",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (categoryId: string, currentProductId: string) => {
    try {
      const { data, error } = await supabase
        .from('products_2025_11_10_14_11')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          stock,
          type,
          billing_type,
          categories_2025_11_10_14_11(name)
        `)
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .neq('id', currentProductId)
        .limit(3);

      if (error) throw error;

      const formattedProducts = data?.map(product => ({
        ...product,
        category_name: product.categories_2025_11_10_14_11?.name
      })) || [];

      setRelatedProducts(formattedProducts);
    } catch (error: any) {
      console.error('Error fetching related products:', error);
    }
  };

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      type: product.type,
      image_url: product.image_url
    });
    toast({
      title: "Producto agregado",
      description: `${product.name} se agregó al carrito`,
    });
  };

  const getBillingTypeLabel = (billingType?: string) => {
    const labels = {
      'monthly': 'Mensual',
      'quarterly': 'Trimestral',
      'biannual': 'Semestral',
      'annual': 'Anual',
      'one_time': 'Pago único'
    };
    return labels[billingType as keyof typeof labels] || 'Pago único';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-96 bg-muted rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="h-20 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Producto no encontrado</h1>
          <p className="text-muted-foreground mb-8">
            El producto que buscas no existe o no está disponible.
          </p>
          <Link to="/productos">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Productos
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary">Inicio</Link>
        <span>/</span>
        <Link to="/productos" className="hover:text-primary">Productos</Link>
        <span>/</span>
        <span className="text-foreground">{product.name}</span>
      </div>

      {/* Product Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Product Image */}
        <div className="space-y-4">
          <div className="aspect-square rounded-lg overflow-hidden bg-muted">
            {product.image_url ? (
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          {product.category_name && (
            <Badge variant="secondary" className="w-fit">
              {product.category_name}
            </Badge>
          )}
          
          <div>
            <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-3xl font-bold text-primary">
                ${product.price.toFixed(2)}
              </span>
              {product.type === 'service' && product.billing_type && (
                <Badge variant="outline">
                  {getBillingTypeLabel(product.billing_type)}
                </Badge>
              )}
            </div>
          </div>

          {/* Stock Status */}
          {product.type === 'product' && (
            <div className="flex items-center gap-2">
              {product.stock > 0 ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  ✓ En Stock ({product.stock} disponibles)
                </Badge>
              ) : (
                <Badge variant="outline" className="text-red-600 border-red-600">
                  ✗ Agotado
                </Badge>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Descripción</h3>
            <HTMLContent 
              content={product.description} 
              className="text-muted-foreground leading-relaxed"
            />
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium">Calidad Premium</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Garantía Incluida</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Truck className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium">Envío Gratis</span>
            </div>
          </div>

          {/* Add to Cart */}
          <div className="space-y-4">
            <Button 
              size="lg"
              className="w-full"
              onClick={() => handleAddToCart(product)}
              disabled={product.type === 'product' && product.stock === 0}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {product.type === 'product' && product.stock === 0 
                ? 'Agotado' 
                : 'Agregar al Carrito'
              }
            </Button>
            
            <p className="text-sm text-muted-foreground text-center">
              {product.type === 'service' 
                ? 'Te contactaremos para coordinar el servicio'
                : 'Envío gratis a toda Colombia'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div>
          <Separator className="mb-8" />
          <h2 className="text-2xl font-bold mb-6">Productos Relacionados</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedProducts.map((relatedProduct) => (
              <Card key={relatedProduct.id} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="h-48 rounded-lg overflow-hidden mb-4 bg-muted">
                    {relatedProduct.image_url ? (
                      <img 
                        src={relatedProduct.image_url} 
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg line-clamp-2">{relatedProduct.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    <HTMLContent content={relatedProduct.description} />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-bold text-primary">
                      ${relatedProduct.price.toFixed(2)}
                    </span>
                  </div>
                  <Link to={`/producto/${relatedProduct.id}`}>
                    <Button variant="outline" className="w-full">
                      Ver Detalles
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;